import { AppContext, useMatchParams } from "@cdv/jazz-ui";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ReactElement, useContext, useState } from "react";
import { Resource } from "../utils";
import { translateAction, translateField, translateSnackbar } from "@cdv/jazz-i18n";
import AuthContext from "../App/Auth/AuthContext";
import { useSnackbar } from "notistack";
import { CurrentUser } from "../../api";
import { useNavigate } from "react-router-dom";

export default function Content(): ReactElement {
  const { setUser } = useContext(AuthContext);
  const [code, setCode] = useState("");
  const [fetching, setFetching] = useState(false);
  const [active, setActive] = useState<CurrentUser | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { resource } = useMatchParams();
  const navigate = useNavigate();
  const [terms, setTerms] = useState(false);

  const handleLogin = async () => {
    setFetching(true);

    try {
      const response = await fetch("/api-auth/auth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code,
          action: "register",
        }),
      });

      if (response.status === 401) {
        enqueueSnackbar(translateSnackbar(resource, "invalidCode"), { variant: "error" });
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const data = await response.json();

      if (data.token && data.user) {
        localStorage.setItem("token", data.token);
        setActive(data.user);
      }
    } catch (error) {
      console.error("Activation failed:", error);

      enqueueSnackbar(translateSnackbar(resource, "invalidCode"), { variant: "error" });
    } finally {
      setFetching(false);
    }
  };

  const handleOk = () => {
    if (!active) {
      return;
    }

    setUser(active);
    window.location.href = "/";
  };

  if (active) {
    return (
      <Stack
        sx={{
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          py: 2,
        }}
        spacing={2}
      >
        <img src="/assets/logo_stressoff.svg" alt="Logo" style={{ maxHeight: "81px" }} />

        <img src="/assets/anim_aktivace_OK.svg" alt="Aktivácia" style={{ maxHeight: "260px" }} />

        <Typography variant="h5" sx={{ textAlign: "center" }}>
          Aplikace byla úspěšně
          <br />
          aktivována
        </Typography>

        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
          }}
          spacing={3}
        >
          <Button variant="contained" size="large" sx={{ minWidth: "211px" }} onClick={handleOk}>
            {translateAction(resource, "ok")}
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        alignItems: "center",
        justifyContent: "center",
        py: 2,
      }}
      spacing={3}
    >
      <img src="/assets/logo_stressoff.svg" alt="Logo" style={{ maxHeight: "81px" }} />

      <img src="/assets/01_ilustr_aktiv.svg" alt="Aktivácia" style={{ maxHeight: "260px" }} />

      <Typography variant="h5" sx={{ textAlign: "center" }}>
        Aktivace
      </Typography>

      <Typography sx={{ textAlign: "center" }}>
        Použijte aktivační kód
        <br />
        pro spuštění aplikace
      </Typography>

      <Stack
        sx={{
          alignItems: "center",
          justifyContent: "center",
        }}
        spacing={3}
      >
        <TextField
          label={translateField(resource, "code")}
          type="number"
          size="small"
          fullWidth
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />

        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={terms} onChange={(e) => setTerms(e.target.checked)} />}
            label={
              <>
                Souhlasím s <a href="/gdpr">podmínkami</a>
              </>
            }
          />
        </FormGroup>

        <Button
          variant="contained"
          size="large"
          sx={{ minWidth: "211px" }}
          disabled={!code || !terms || fetching}
          loading={fetching}
          onClick={handleLogin}
        >
          {translateAction(resource, "register")}
        </Button>
      </Stack>
    </Stack>
  );
}
